/* @prep-v1 — PrEP, admin report.
 *
 * Same look as the sick-leave and renewals reports: month picker, totals,
 * per-doctor table, filter chips, request table. Statuses and outcomes are
 * read from the backend and only labelled here — no rule is restated.
 *
 * NO CLINICAL CONTENT. The endpoint returns visit type, status, outcome,
 * dates and money only — never answers, results, files or a diagnosis.
 * Keep it that way: PrEP data is GDPR art. 9 and the claiming doctor's alone.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { prepAdminAPI } from '../services/api';

const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280', fontWeight: 700, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const td = { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthRange = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return [`${ym}-01`, `${ym}-${String(last).padStart(2, '0')}`];
};
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('el-GR', { timeZone: 'Europe/Athens', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const hrs = (h) => (h == null || h < 0 ? '—' : h < 1 ? `${Math.round(h * 60)}′` : `${h.toFixed(1).replace('.', ',')} ω`);
const euros = (n) => `€${Number(n || 0).toFixed(2)}`;

const VISIT = {
  INITIAL: 'Πρώτη συνταγή', RESTART: 'Επανέναρξη', ANNUAL: 'Ετήσια', MONTH1: '1ος μήνας', RENEWAL: 'Ανανέωση', STOP: 'Διακοπή',
};
const STATUS = {
  awaiting_referral: ['Αναμονή παραπεμπτικού', '#E07B2A'],
  awaiting_results: ['Αναμονή αποτελεσμάτων', '#1E3A5F'],
  results_uploaded: ['Αξιολόγηση', '#1E3A5F'],
  info_requested: ['Αναμονή ασθενή', '#6b7280'],
  awaiting_confirmation: ['Αναμονή επιβεβαίωσης', '#6b7280'],
  completed: ['Ολοκληρώθηκε', '#0D9A6A'],
  expired: ['Έληξε', '#B91C1C'],
  closed_no_upload: ['Χωρίς αποτελέσματα 30 ημ.', '#6b7280'],
  closed_no_reply: ['Χωρίς απάντηση 30 ημ.', '#6b7280'],
};
const DECISION = {
  issued: ['Συνταγή', '#0D9A6A'],
  referred: ['Παραπομπή', '#E07B2A'],
  declined: ['Απόρριψη', '#B91C1C'],
  // The backend never sends 'reactive' to admin: a clinical outcome next to a
  // name is health data. Such a case arrives as 'closed'.
  closed: ['Έκλεισε από τον γιατρό', '#6b7280'],
  stopped: ['Διακοπή', '#6b7280'],
};
const REFUND = {
  refunded: ['Επιστράφηκαν', '#C0185A'],
  pending: ['Επιστροφή σε εξέλιξη', '#E07B2A'],
  failed: ['ΑΠΕΤΥΧΕ — χειροκίνητα στο Viva', '#B91C1C'],
};
const LIVE = ['awaiting_referral', 'awaiting_results', 'results_uploaded', 'info_requested', 'awaiting_confirmation'];

const Pill = ({ text, color }) => (
  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
    color, background: color + '18', whiteSpace: 'nowrap' }}>{text}</span>
);
const Stat = ({ value, label, warn }) => (
  <div style={{ background: '#fff', border: `1px solid ${warn ? '#FCA5A5' : '#e5e7eb'}`, borderRadius: 10, padding: '12px 16px', minWidth: 130, flex: '1 1 130px' }}>
    <div style={{ fontSize: 22, fontWeight: 700, color: warn ? '#B91C1C' : '#0F1F3D' }}>{value}</div>
    <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
  </div>
);

export default function PrepReportPage() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const [from, to] = monthRange(month);
    prepAdminAPI.list({ from, to })
      .then((r) => { if (!cancelled) setData(r.data?.data || null); })
      .catch(() => { if (!cancelled) { setData(null); toast.error('Αδυναμία φόρτωσης PrEP'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month]);

  const s = data?.summary;
  const all = data?.requests || [];
  const rows = all.filter((r) => {
    if (filter === 'live') return LIVE.includes(r.status);
    if (filter === 'issued') return r.decision === 'issued';
    if (filter === 'refunded') return ['refunded', 'pending', 'failed'].includes(r.refundStatus);
    return true;
  });

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>PrEP</h1>
          <p style={{ color: '#6b7280', fontSize: 13, margin: '6px 0 0' }}>
            Επισκέψεις που πληρώθηκαν τον μήνα (ώρα Αθήνας), μαζί με τις δωρεάν διακοπές. Χωρίς απαντήσεις, αποτελέσματα ή αρχεία — αυτά τα βλέπει μόνο ο γιατρός.{' '}
            <Link to="/sick-leave">Αναρρωτικές →</Link>
          </p>
        </div>
        <label style={{ fontSize: 13, color: '#374151' }}>
          Μήνας{' '}
          <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
            style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
        </label>
      </div>

      {s && s.refundsFailed > 0 && (
        <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {s.refundsFailed} επιστροφή/ές απέτυχαν στο Viva και θέλουν χειροκίνητη επιστροφή (έχει σταλεί email στο support).
        </div>
      )}

      {s && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
          <Stat value={s.requests} label="Επισκέψεις" />
          <Stat value={s.live} label="Σε εξέλιξη τώρα" />
          <Stat value={s.issued} label="Συνταγές" />
          <Stat value={`${s.referred} / ${s.declined}`} label="Παραπομπές / Απορρίψεις" />
          <Stat value={s.expiredRefunded} label="Επιστροφές: χωρίς παραπεμπτικό σε 24 ω" warn={s.expiredRefunded > 0} />
          <Stat value={`${s.closedNoUpload} / ${s.closedNoReply}`} label="Έκλεισαν: χωρίς αποτελέσματα / απάντηση" />
          <Stat value={euros(s.keptEuros)} label="Εισπράξεις (χωρίς επιστροφές)" />
          <Stat value={euros(s.doctorEuros)} label="Αμοιβές γιατρών" />
          <Stat value={euros(s.platformEuros)} label="DocNow" />
          <Stat value={s.onProgramme} label="Ενεργοί στο πρόγραμμα (σύνολο)" />
          <Stat value={s.abandonedCheckouts} label="Δεν ολοκλήρωσαν πληρωμή (καμία χρέωση)" />
        </div>
      )}

      <h2 style={{ fontSize: 16, fontWeight: 700, margin: '4px 0 8px' }}>Ανά γιατρό</h2>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflowX: 'auto', marginBottom: 22 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Γιατρός</th>
              <th style={th}>Ολοκληρώθηκαν</th>
              <th style={th}>Συνταγές / Παραπομπές / Απορρίψεις / Άλλο</th>
              <th style={th}>Αμοιβή</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td style={td} colSpan={4}>Φόρτωση…</td></tr>}
            {!loading && (data?.byDoctor || []).length === 0 && <tr><td style={td} colSpan={4}>Δεν υπάρχουν ολοκληρωμένες επισκέψεις αυτόν τον μήνα.</td></tr>}
            {!loading && (data?.byDoctor || []).map((d) => (
              <tr key={d.doctor}>
                <td style={td}>{d.doctor}</td>
                <td style={td}>{d.completed}</td>
                <td style={td}>{d.issued} / {d.referred} / {d.declined} / {d.closed}</td>
                <td style={td}>{euros(d.doctorEuros)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, margin: '4px 0 8px' }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Επισκέψεις</h2>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[['all', 'Όλες'], ['live', 'Σε εξέλιξη'], ['issued', 'Συνταγές'], ['refunded', 'Επιστροφές']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFilter(k)}
              style={{ padding: '5px 12px', borderRadius: 16, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                border: '1px solid ' + (filter === k ? '#C0185A' : '#e5e7eb'),
                background: filter === k ? '#C0185A' : '#fff', color: filter === k ? '#fff' : '#374151' }}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Πληρωμή</th>
              <th style={th}>Ασθενής</th>
              <th style={th}>Επίσκεψη</th>
              <th style={th}>Γιατρός</th>
              <th style={th}>Κατάσταση</th>
              <th style={th}>Έκβαση</th>
              <th style={th}>Ποσό</th>
              <th style={th}>Επιστροφή</th>
              <th style={th}>Παραπεμπτικό σε</th>
              <th style={th}>Απόφαση σε</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td style={td} colSpan={10}>Φόρτωση…</td></tr>}
            {!loading && rows.length === 0 && <tr><td style={td} colSpan={10}>Καμία επίσκεψη.</td></tr>}
            {!loading && rows.map((r) => {
              const st = STATUS[r.status] || [r.status, '#6b7280'];
              const dc = r.decision ? DECISION[r.decision] : null;
              const rf = REFUND[r.refundStatus];
              return (
                <tr key={r.id} style={{ background: r.refundStatus === 'failed' ? '#FEF2F2' : 'transparent' }}>
                  <td style={td}>{fmtDateTime(r.paidAt)}</td>
                  <td style={td}>{r.patientName || '—'}</td>
                  <td style={td}>{VISIT[r.visitType] || r.visitType}</td>
                  <td style={td}>{r.doctorName || '—'}</td>
                  <td style={td}><Pill text={st[0]} color={st[1]} /></td>
                  <td style={td}>{dc ? <Pill text={dc[0]} color={dc[1]} /> : '—'}</td>
                  <td style={td}>{r.amountEuros ? euros(r.amountEuros) : 'δωρεάν'}</td>
                  <td style={td}>{rf ? <Pill text={rf[0]} color={rf[1]} /> : '—'}</td>
                  <td style={td}>{hrs(r.hoursToReferral)}</td>
                  <td style={td}>{hrs(r.hoursToDecision)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p style={{ color: '#6b7280', fontSize: 12, marginTop: 14 }}>
        Γιατροί στην υπηρεσία: <Link to="/doctors">Ιατροί</Link> → γιατρός → Κλινικές → «PrEP».
        Αμοιβή: €30 ανά ιατρική εκτίμηση, για κάθε τύπο επίσκεψης (γιατρός €18,75) · διακοπή δωρεάν.
        Αν δεν εκδοθεί παραπεμπτικό σε 24 ώρες, επιστρέφεται αυτόματα. Μια αποτυχημένη επιστροφή χρειάζεται χειροκίνητη ενέργεια.
      </p>
    </div>
  );
}
